/*
 * Cálculo de frete (bloco "Calculo Frete").
 * Frete fixo para todo o Brasil; o prazo vem da API consulta-prazo (serverless da loja, que
 * simula o envio por CEP): opção mais barata + dias úteis de produção, com mais N dias úteis no fim.
 * Se a API não responder (rede, CEP inválido, CEP sem opção), usa a tabela por região.
 * Cidade/UF pelo ViaCEP. Dias úteis descontam fins de semana e feriados nacionais.
 * O CEP não fica salvo: o resultado aparece só na hora da consulta e some depois de alguns segundos
 * ou com um toque fora do quadro (a consulta fica registrada nos atributos do carrinho).
 * No estúdio (passo da camiseta) o quadro vem recolhido: tocar no título abre; tocar fora fecha.
 */

const VIACEP = (cep) => `https://viacep.com.br/ws/${cep}/json/`;
const OLD_STORAGE_KEY = 'cb_frete_cep';
const RESET_MS = 15000;
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

// Plano B: prazo total (produção + entrega) por região, como era antes
const REGION_DAYS = { N: [8, 17], NE: [8, 16], SPRJ: [7, 14], OUTRAS: [7, 14] };
const UF_REGION = {
  AC: 'N', AM: 'N', AP: 'N', PA: 'N', RO: 'N', RR: 'N', TO: 'N',
  AL: 'NE', BA: 'NE', CE: 'NE', MA: 'NE', PB: 'NE', PE: 'NE', PI: 'NE', RN: 'NE', SE: 'NE',
  SP: 'SPRJ', RJ: 'SPRJ'
};
const DEFAULT_DAYS = [8, 17];

const digits = (v) => String(v || '').replace(/\D+/g, '').slice(0, 8);
const mask = (v) => { const d = digits(v); return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d; };

// versões antigas guardavam o CEP no navegador: apaga
try { localStorage.removeItem(OLD_STORAGE_KEY); } catch (e) { /* segue */ }

/* ---------------- dias úteis ---------------- */
const pad = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const FIXED_HOLIDAYS = ['01-01', '04-21', '05-01', '09-07', '10-12', '11-02', '11-15', '11-20', '12-25'];
const holidayCache = {};

function easter(y) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(y, month - 1, day, 12);
}

// feriados nacionais + Carnaval (seg e ter), Sexta-feira Santa e Corpus Christi
function holidays(y) {
  if (!holidayCache[y]) {
    const e = easter(y);
    const rel = (n) => { const d = new Date(e); d.setDate(d.getDate() + n); return dayKey(d); };
    holidayCache[y] = new Set([...FIXED_HOLIDAYS.map((s) => `${y}-${s}`), rel(-48), rel(-47), rel(-2), rel(60)]);
  }
  return holidayCache[y];
}

const isBusinessDay = (d) => d.getDay() !== 0 && d.getDay() !== 6 && !holidays(d.getFullYear()).has(dayKey(d));

function addBusinessDays(from, n) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 12);
  let left = Math.max(0, n);
  while (left > 0) {
    d.setDate(d.getDate() + 1);
    if (isBusinessDay(d)) left -= 1;
  }
  return d;
}

function rangeLabel(a, b) {
  if (dayKey(a) === dayKey(b)) return `em ${a.getDate()} de ${MONTHS[a.getMonth()]}`;
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `entre ${a.getDate()} e ${b.getDate()} de ${MONTHS[b.getMonth()]}`;
  }
  return `entre ${a.getDate()} de ${MONTHS[a.getMonth()]} e ${b.getDate()} de ${MONTHS[b.getMonth()]}`;
}

/* ---------------- consultas ---------------- */
async function lookupPlace(cep) {
  try {
    const r = await fetch(VIACEP(cep), { headers: { Accept: 'application/json' } });
    if (!r.ok) return undefined;
    const j = await r.json();
    if (j && j.erro) return null; // CEP não existe
    return { city: String(j.localidade || '').trim(), uf: String(j.uf || '').trim().toUpperCase() };
  } catch (e) {
    return undefined; // sem resposta
  }
}

// opção mais barata da simulação (ou null). Resposta: { shipping_simulation: { delivery_options: [...] } }
async function cheapestOption(cep, api) {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), 6000);
  try {
    const url = `${api}${api.includes('?') ? '&' : '?'}cep=${mask(cep)}`;
    const r = await fetch(url, { headers: { Accept: 'application/json' }, signal: ctl.signal });
    if (!r.ok) return null;
    const j = await r.json();
    const list = ((j && (j.shipping_simulation || j).delivery_options) || [])
      .filter((o) => Number.isFinite(Number(o.cost)) && Number.isFinite(Number(o.estimated_business_days)));
    if (!list.length) return null;
    list.sort((x, y) => Number(x.cost) - Number(y.cost) || Number(x.estimated_business_days) - Number(y.estimated_business_days));
    return { days: Number(list[0].estimated_business_days), name: list[0].name || '' };
  } catch (e) {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

async function saveCartAttributes(attributes) {
  try {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes })
    });
  } catch (e) { /* não atrapalha o cálculo */ }
}

/* ---------------- componente ---------------- */
class CalculoFrete extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    const $ = (s) => this.querySelector(s);
    this.form = $('.cbf__form');
    this.input = $('.cbf__input');
    this.btn = $('.cbf__btn');
    this.msg = $('.cbf__msg');
    this.result = $('.cbf__result');
    this.r1 = $('.cbf__r1');
    this.r2 = $('.cbf__r2');
    this.cepOut = $('.cbf__cep');
    if (!this.form || !this.input) return;

    this.cfg = {
      api: this.dataset.api || 'https://consulta-prazo.vercel.app/api/frete',
      prod: parseInt(this.dataset.prod, 10) || 0,
      range: parseInt(this.dataset.range, 10) || 0
    };

    this.form.addEventListener('submit', (e) => { e.preventDefault(); this.calculate(); });
    this.input.addEventListener('input', (e) => {
      const before = this.input.value;
      this.input.value = mask(before);
      this.setError('');
      // digitou ou colou os 8 números: calcula sem precisar do botão
      if (digits(this.input.value).length === 8 && e.inputType !== 'deleteContentBackward') this.calculate();
    });
    $('.cbf__edit').addEventListener('click', () => {
      this.reset();
      this.expand(true);
    });

    // com o resultado na tela: tocar dentro do quadro segura o tempo; tocar fora limpa
    this._outside = (e) => {
      if (this.dataset.state !== 'result') return;
      if (this.contains(e.target)) this.armReset();
      else this.reset();
    };
    // no estúdio: recolhido; o título abre e fecha (o CSS do estúdio esconde o formulário fechado)
    this.head = $('.cbf__head');
    if (this.head) {
      this.head.addEventListener('click', () => {
        if (!document.documentElement.classList.contains('cb-studio-open')) return;
        this.expand(!this.hasAttribute('data-open'));
      });
    }
    this._away = (e) => {
      if (!this.contains(e.target) && this.dataset.state !== 'result' && !this._pending) this.expand(false);
    };

    // o navegador pode devolver o campo preenchido ao voltar para a página
    window.addEventListener('pageshow', () => this.reset());
    this.reset();
  }

  expand(open) {
    this.toggleAttribute('data-open', open);
    document.removeEventListener('click', this._away, true);
    if (!open) return;
    setTimeout(() => document.addEventListener('click', this._away, true), 0);
    // no mesmo toque, para o teclado do celular abrir
    this.input.focus();
  }

  armReset() {
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this.reset(), RESET_MS);
  }

  reset() {
    clearTimeout(this._timer);
    document.removeEventListener('click', this._outside, true);
    if (this.head) this.expand(false);
    this._pending = null;
    this.dataset.state = 'form';
    delete this.dataset.source;
    this.result.hidden = true;
    this.input.value = '';
    this.setError('');
    this.busy(false);
  }

  setError(text) {
    this.msg.textContent = text;
    if (text) this.setAttribute('data-error', ''); else this.removeAttribute('data-error');
    if (text) { this.dataset.state = 'form'; this.result.hidden = true; }
  }

  busy(on) {
    this.btn.setAttribute('aria-busy', String(on));
    this.btn.querySelector('span').textContent = on ? 'Calculando…' : 'Calcular';
  }

  async calculate() {
    const cep = digits(this.input.value);
    if (cep.length !== 8) {
      this.setError('Digite os 8 números do CEP.');
      this.input.focus();
      return;
    }
    if (this._pending === cep) return;
    this._pending = cep;
    this.setError('');
    this.busy(true);

    const [place, option] = await Promise.all([lookupPlace(cep), cheapestOption(cep, this.cfg.api)]);
    if (this._pending !== cep) return; // limpo no meio da consulta
    this._pending = null;
    this.busy(false);

    if (place === null && !option) {
      this.setError('CEP não encontrado. Confira os números.');
      return;
    }

    let span;
    let source;
    if (option) {
      const min = option.days + this.cfg.prod;
      span = [min, min + this.cfg.range];
      source = 'simulacao';
    } else if (place && place.uf) {
      span = REGION_DAYS[UF_REGION[place.uf] || 'OUTRAS'] || DEFAULT_DAYS;
      source = 'tabela';
    } else {
      this.setError('Não conseguimos calcular agora. Tente de novo em instantes.');
      return;
    }

    const now = new Date();
    const label = rangeLabel(addBusinessDays(now, span[0]), addBusinessDays(now, span[1]));
    const where = place && place.city ? `${place.city}/${place.uf}` : `o CEP ${mask(cep)}`;

    this.r1.textContent = `Entrega estimada para ${where}:`;
    this.r2.innerHTML = '';
    this.r2.append(Object.assign(document.createElement('b'), { textContent: label }));
    this.cepOut.textContent = `CEP ${mask(cep)}`;
    this.result.hidden = false;
    this.dataset.state = 'result';
    this.dataset.source = source;
    if (document.activeElement === this.input) this.input.blur();

    const detail = { cep: mask(cep), city: place && place.city, uf: place && place.uf, days: span, label, source };
    this.dispatchEvent(new CustomEvent('cb:frete', { bubbles: true, detail }));
    saveCartAttributes({
      __frete_consultado_em: now.toISOString(),
      __frete_cep: mask(cep),
      __frete_destino: place && place.city ? `${place.city} - ${place.uf}` : '',
      __frete_prazo_estimado: label
    });

    this.armReset();
    setTimeout(() => document.addEventListener('click', this._outside, true), 0);
  }
}

if (!customElements.get('calculo-frete')) customElements.define('calculo-frete', CalculoFrete);